/**
 * ISG IT Helpdesk — Frontend Entry Point
 *
 * Enterprise helpdesk admin app for IT staff.
 * Uses @isg/ui-kit for shared CSS, components, and services.
 */

// --- Styles ---
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';
import './styles/tiptap.css';
import './styles/mobile.css';

// --- Services & Utils ---
import { router, store, auth, api , checkEnvBanner } from '@isg/ui-kit';
import { syncPushSubscription } from '@isg/ui-kit';
import { maybeForcePasswordChange } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';
import { createNotificationBell, createNotificationSidebar } from './components/notification-sidebar.js';
import { renderAIAssistant } from './components/ai-assistant-widget.js';
import { t, initI18n, onLocaleChange } from './i18n/index.js';

/* SSO: capture handoff from intranet broker */
auth.captureSSOHandoff();

// --- Lazy-loaded pages ---
const pages = {
  dashboard:       () => import('./pages/dashboard.js'),
  triage:          () => import('./pages/triage.js'),
  tickets:         () => import('./pages/tickets.js'),
  ticketDetail:    () => import('./pages/ticket-detail.js'),
  newTicket:       () => import('./pages/new-ticket.js'),
  sla:             () => import('./pages/sla.js'),
  assignmentRules: () => import('./pages/assignment-rules.js'),
  faq:             () => import('./pages/faq.js'),
  lookups:         () => import('./pages/lookups.js'),
  itCatalogue:     () => import('./pages/it-catalogue.js'),
  equipment:       () => import('./pages/equipment.js'),
  itDocuments:     () => import('./pages/documents.js'),
  hotTopics:       () => import('./pages/hot-topics.js'),
  agents:          () => import('./pages/agents.js'),
  reports:         () => import('./pages/reports.js'),
  reportArchive:   () => import('./pages/report-archive.js'),
  settings:        () => import('./pages/settings.js'),
  notifications:   () => import('./pages/notifications.js'),
  emailRules:      () => import('./pages/email-rules.js'),
  login:           () => import('./pages/login.js'),
};

// ── Navigation config ────────────────────────────────────────────────
// Built at RENDER time (never at module scope) so every label is t()'d in
// the reader's locale.
// Agents get a stripped-down shell: their ticket queue only. Dashboards,
// on-behalf creation and every Admin surface are lead/admin-tier (the
// backend enforces the same split — this just keeps the nav honest).
function navConfigFor(role) {
  if (role === 'agent') {
    return [
      {
        section: null,
        items: [
          { path: '/', label: t('nav.my_dashboard'), icon: 'layout-dashboard' },
          { path: '/tickets', label: t('nav.my_tickets'), icon: 'inbox' },
          { path: '/notifications', label: t('nav.my_notifications'), icon: 'bell' },
        ],
      },
    ];
  }
  return [
    {
      section: null,
      items: [
        { path: '/', label: t('nav.dashboard'), icon: 'layout-dashboard' },
        { path: '/triage', label: t('nav.triage'), icon: 'target' },
        { path: '/tickets', label: t('nav.tickets'), icon: 'inbox' },
        { path: '/tickets/new', label: t('nav.new_ticket'), icon: 'plus' },
        { path: '/notifications', label: t('nav.my_notifications'), icon: 'bell' },
      ],
    },
    {
      section: t('nav.section_admin'),
      items: [
        { path: '/reports', label: t('nav.reports'), icon: 'bar-chart-2' },
        { path: '/report-archive', label: t('nav.report_archive'), icon: 'book-open' },
        { path: '/sla', label: t('nav.sla_policies'), icon: 'clock' },
        { path: '/assignment-rules', label: t('nav.assignment_rules'), icon: 'git-branch' },
        { path: '/faq', label: t('nav.faq'), icon: 'book-open' },
        { path: '/ticket-setup', label: t('nav.ticket_setup'), icon: 'sliders' },
        { path: '/new-starter-lists', label: t('nav.new_starter_lists'), icon: 'user-plus' },
        { path: '/equipment', label: t('nav.equipment'), icon: 'monitor' },
        { path: '/documents', label: t('nav.it_documents'), icon: 'file-text' },
        { path: '/hot-topics', label: t('nav.hot_topics'), icon: 'megaphone' },
        { path: '/agents', label: t('nav.agents'), icon: 'users' },
        { path: '/email-rules', label: t('nav.email_rules'), icon: 'mail' },
        { path: '/settings', label: t('nav.settings'), icon: 'settings' },
      ],
    },
  ];
}

// ── Render a page module into #main-content ──────────────────────────
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">${t('common.something_went_wrong')}</p>
          <p>${err.message || t('app.error.page_failed')}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">${t('app.error.reload')}</button>
        </div>
      </div>
    `;
  }
}

// ── App shell ────────────────────────────────────────────────────────
function renderAppShell() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: t('app.name') });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: navConfigFor(store.get('helpdesk_role')),
    appTitle: t('app.name'),
    fallbackInitial: 'H',
  });

  // Main wrapper with toolbar
  const mainWrapper = document.createElement('div');
  mainWrapper.style.cssText = 'display:flex;flex-direction:column;flex:1;min-width:0;';

  const toolbar = document.createElement('div');
  toolbar.className = 'global-toolbar';
  toolbar.style.cssText = 'display:flex;align-items:center;justify-content:flex-end;padding:0 var(--space-4);height:48px;border-bottom:1px solid var(--border-secondary);background:var(--surface-primary);gap:var(--space-2);flex-shrink:0;';

  const bellBtn = createNotificationBell();
  toolbar.appendChild(bellBtn);

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  mainWrapper.appendChild(toolbar);
  mainWrapper.appendChild(main);

  const notifSidebar = createNotificationSidebar();

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(mainWrapper);
  app.appendChild(layout);
  app.appendChild(notifSidebar);

}

// ── Sidebar count badges (Tickets + Triage) ──────────────────────────

/** Add, update or remove a count bubble on a sidebar link. */
function setNavBadge(href, count, colour) {
  var links = document.querySelectorAll(
    '.sidebar a[href="' + href + '"], .sidebar [data-link][href="' + href + '"]');
  links.forEach(function(link) {
    var badge = link.querySelector('.nav-count-badge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'nav-count-badge';
        badge.style.cssText = 'color:#fff;font-size:0.65rem;font-weight:700;min-width:18px;'
          + 'height:18px;border-radius:9px;display:inline-flex;align-items:center;'
          + 'justify-content:center;padding:0 5px;margin-left:auto;transition:transform 0.2s;';
        link.style.display = 'flex';
        link.style.alignItems = 'center';
        link.appendChild(badge);
      }
      badge.style.background = colour || 'var(--red-500)';
      var prev = badge.textContent;
      var next = count > 99 ? '99+' : String(count);
      badge.textContent = next;
      // A brief pulse when the number GOES UP, so a lead glancing at the
      // sidebar notices a new arrival rather than having to read it.
      if (prev && prev !== next && parseInt(next, 10) > parseInt(prev, 10)) {
        badge.style.transform = 'scale(1.45)';
        setTimeout(function() { badge.style.transform = 'scale(1)'; }, 220);
      }
    } else if (badge) {
      badge.remove();
    }
  });
}

function startTicketBadgePolling() {
  async function updateBadge() {
    var isAgent = store.get('helpdesk_role') === 'agent';
    try {
      var count;
      if (isAgent) {
        // Agents: THEIR open queue (the dashboard endpoint is lead-tier)
        var mine = await api.get('/tickets?status=open,assigned,in_progress&per_page=1');
        count = mine.total || 0;
      } else {
        var data = await api.get('/dashboard');
        count = (data.overview && data.overview.open) || 0;
      }
      setNavBadge('/tickets', count);
    } catch (e) { /* ignore */ }

    // Triage bubble — unassigned tickets awaiting a lead. Agents never see
    // the Triage nav item, so don't spend a request on it for them.
    if (isAgent) return;
    try {
      // active=true == the server's "not finished" umbrella. Without it this
      // counted closed tickets that were never assigned, so the bubble read
      // 14 while the Triage page showed 8.
      var un = await api.get('/tickets?unassigned=true&active=true&per_page=1');
      // Amber, not red: this is a queue to work, not an alarm — the red
      // Tickets badge already carries "there is a backlog".
      setNavBadge('/triage', un.total || 0, 'var(--amber-500)');
    } catch (e) { /* ignore */ }
  }

  updateBadge();
  setInterval(updateBadge, 60000); // slow fallback — live events drive it now

  // Real-time: any ticket event refreshes both badges (debounced)
  import('./components/live-stream.js').then(function(mod) {
    var t = null;
    mod.onTicketEvent(function() {
      clearTimeout(t);
      t = setTimeout(updateBadge, 1000);
    });
  }).catch(function() { /* stream unavailable — polling still runs */ });
}

// ── Init ─────────────────────────────────────────────────────────────
async function init() {
  await loadBranding().catch(() => {});
  const hasSession = await auth.checkSession();

  // Boot i18n with the signed-in user's stored locale BEFORE any shell or
  // page renders (signed out: null, which falls back to the browser/default).
  await initI18n(hasSession ? store.get('user') : null).catch(() => {});

  // index.html's static <title> is English (painted before the locale is
  // known); from here on the tab is named in the reader's language.
  document.title = t('app.title');
  onLocaleChange(() => { document.title = t('app.title'); });

  // Re-register this browser's push subscription if it holds one. Push
  // services rotate endpoints and the service worker's own retry can't
  // always send a CSRF token, so this boot-time re-POST is the reliable
  // repair. Not awaited — it must never delay first paint, and it
  // no-ops when the user has never granted permission.
  if (hasSession) syncPushSubscription('helpdesk');

  router
    .on('/', (params) => renderPage(pages.dashboard, params))
    .on('/triage', (params) => renderPage(pages.triage, params))
    .on('/tickets', (params) => renderPage(pages.tickets, params))
    .on('/tickets/new', (params) => renderPage(pages.newTicket, params))
    .on('/tickets/:id', (params) => renderPage(pages.ticketDetail, params))
    .on('/sla', (params) => renderPage(pages.sla, params))
    .on('/assignment-rules', (params) => renderPage(pages.assignmentRules, params))
    .on('/faq', (params) => renderPage(pages.faq, params))
    .on('/ticket-setup', (params) => renderPage(pages.lookups, params))
    .on('/new-starter-lists', (params) => renderPage(pages.itCatalogue, params))
    .on('/equipment', (params) => renderPage(pages.equipment, params))
    // Old paths kept as redirects: they are bookmarked, and an agent guard
    // bounce is indistinguishable from a 404 to whoever clicks a dead link.
    .on('/categories', () => router.navigate('/ticket-setup?tab=categories'))
    .on('/ticket-types', () => router.navigate('/ticket-setup?tab=types'))
    .on('/documents', (params) => renderPage(pages.itDocuments, params))
    .on('/hot-topics', (params) => renderPage(pages.hotTopics, params))
    .on('/agents', (params) => renderPage(pages.agents, params))
    .on('/reports', (params) => renderPage(pages.reports, params))
    .on('/report-archive', (params) => renderPage(pages.reportArchive, params))
    .on('/email-rules', (params) => renderPage(pages.emailRules, params))
    .on('/settings', (params) => renderPage(pages.settings, params))
    .on('/notifications', (params) => renderPage(pages.notifications, params))
    .on('/login', (params) => renderPage(pages.login, params));

  router.before((path) => {
    // An AUTHENTICATED user must never be left on the login page. Waving
    // /login through unconditionally renders the login FORM inside the app
    // shell — the app looks signed in and signed out at once, and only a
    // manual reload clears it. Not an edge case: api.js bounces here on any
    // 401 while the httpOnly cookie usually survives, so the reload lands
    // authenticated; the SSO broker's ?next and plain bookmarks reach it too.
    // Fixed in isg-app-template 2026-07-18; back-propagated 2026-07-31.
    if (path === '/login' && store.get('isAuthenticated')) {
      router.navigate('/');
      return false;
    }
    if (store.get('helpdesk_role') === 'agent'
        && !/^\/$|^\/tickets(\/\d+)?$/.test(path)) {
      router.navigate('/');
      return false;
    }
    if (path === '/login') return true;
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  router.after(() => {
    setTimeout(() => applyBranding(), 50);
  });

  if (!hasSession) {
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    module.render(document.getElementById('main-content'));
    if (window.location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    // Resolve the caller's helpdesk role BEFORE building the shell so the
    // nav and landing page match their tier.
    try {
      const r = await api.get('/admin/agents/my-role');
      store.set('helpdesk_role', r.role);
    } catch (e) {
      store.set('helpdesk_role', null);
    }
    await maybeForcePasswordChange();
    renderAppShell();
    checkEnvBanner(); // beta/test banner (no-op on live)
    startTicketBadgePolling();
    await router.resolve();
    applyBranding();
    if (store.get('helpdesk_role') !== 'agent') {
      const aiMount = document.createElement('div');
      aiMount.id = 'ai-assistant-mount';
      document.body.appendChild(aiMount);
      renderAIAssistant(aiMount);
    }
  }
}

init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>${t('app.error.app_failed')}</p>
      <button class="btn btn-primary" onclick="location.reload()">${t('app.error.reload')}</button>
    </div>
  `;
});
